import {
  CARRY_OVER_ANCHORS,
  DEFAULT_TOP_OFFSET_MM,
  defaultAnchorForClass,
  isCarryOverAnchor,
  type CarryOverAnchor,
} from '@designdeck/shared';
import { FormField, Input, Select } from '@/components/ui';
import type { AdminProduct } from '@/api/admin';
import { CARRY_OVER_ANCHOR_LABEL } from './ProductCarryOverPanel';

/** A print area's carry-over exceptions; null inherits the product value. */
export interface AreaCarryOverValues {
  carryOverPercent: number | null;
  carryOverAnchor: CarryOverAnchor | null;
  carryOverTopOffsetMm: number | null;
}

/** "inherited" or "exception", with the way back to the product value. */
function ExceptionTag({
  isException,
  onReset,
}: {
  isException: boolean;
  onReset: () => void;
}) {
  if (!isException) {
    return <span className="text-xs text-mute">inherited</span>;
  }
  return (
    <span className="flex items-center gap-2 text-xs">
      <span className="font-semibold text-ink">exception</span>
      <button
        type="button"
        onClick={onReset}
        className="text-ink-2 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Reset to product value
      </button>
    </span>
  );
}

interface AreaCarryOverFieldsProps {
  values: AreaCarryOverValues;
  /** The product the area belongs to: its values are the placeholders. */
  product: Pick<
    AdminProduct,
    | 'productClass'
    | 'carryOverPercent'
    | 'carryOverAnchor'
    | 'carryOverTopOffsetMm'
  >;
  /** A RUNDUM area never takes a percent box; the field is off. */
  isRundum: boolean;
  disabled: boolean;
  onChange: (patch: Partial<AreaCarryOverValues>) => void;
}

/**
 * The three area exceptions to the product carry-over (percent, anchor,
 * top offset) in the print-area sidebar. Each field shows the inherited
 * product value as its placeholder and offers the way back to it.
 */
export function AreaCarryOverFields({
  values,
  product,
  isRundum,
  disabled,
  onChange,
}: AreaCarryOverFieldsProps) {
  const labelWithTag = (
    text: string,
    isException: boolean,
    reset: Partial<AreaCarryOverValues>
  ) => (
    <span className="flex items-center justify-between gap-2">
      <span>{text}</span>
      <ExceptionTag isException={isException} onReset={() => onChange(reset)} />
    </span>
  );
  const anchorPlaceholder = `${
    CARRY_OVER_ANCHOR_LABEL[
      product.carryOverAnchor ??
        defaultAnchorForClass(product.productClass ?? null)
    ]
  } (product${product.carryOverAnchor ? '' : ', default'})`;

  return (
    <div className="border-t border-line pt-3">
      <h3 className="dc-h3 text-sm text-ink">Carry-over exceptions</h3>
      <p className="mt-1 text-xs text-mute">
        The product values apply unless this area sets its own.
      </p>
      <div className="mt-2 space-y-3">
        <FormField
          label={labelWithTag('Percent', values.carryOverPercent !== null, {
            carryOverPercent: null,
          })}
          hint={isRundum ? 'Rundum precedes a percent box.' : undefined}
        >
          <Input
            type="number"
            min={1}
            max={100}
            step={1}
            aria-label="Area carry-over percent"
            value={values.carryOverPercent ?? ''}
            placeholder={
              product.carryOverPercent != null
                ? `${product.carryOverPercent} (product)`
                : 'none (product, default rules)'
            }
            disabled={disabled || isRundum}
            onChange={(e) =>
              onChange({
                carryOverPercent: e.target.value
                  ? Number(e.target.value)
                  : null,
              })
            }
          />
        </FormField>
        <FormField
          label={labelWithTag('Anchor', values.carryOverAnchor !== null, {
            carryOverAnchor: null,
          })}
        >
          <Select
            aria-label="Area carry-over anchor"
            value={values.carryOverAnchor ?? ''}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                carryOverAnchor: isCarryOverAnchor(e.target.value)
                  ? e.target.value
                  : null,
              })
            }
          >
            <option value="">{anchorPlaceholder}</option>
            {CARRY_OVER_ANCHORS.map((anchor) => (
              <option key={anchor} value={anchor}>
                {CARRY_OVER_ANCHOR_LABEL[anchor]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label={labelWithTag(
            'Top offset (mm)',
            values.carryOverTopOffsetMm !== null,
            { carryOverTopOffsetMm: null }
          )}
        >
          <Input
            type="number"
            min={0}
            step={1}
            aria-label="Area carry-over top offset"
            value={values.carryOverTopOffsetMm ?? ''}
            placeholder={`${
              product.carryOverTopOffsetMm ?? DEFAULT_TOP_OFFSET_MM
            } mm (product${product.carryOverTopOffsetMm != null ? '' : ', default'})`}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                carryOverTopOffsetMm: e.target.value
                  ? Number(e.target.value)
                  : null,
              })
            }
          />
        </FormField>
      </div>
    </div>
  );
}
